import apiClient from '../client'
import type { Lead, LeadFilters, PaginatedResponse } from '@/types'

// Backend response type
interface BackendLeadsResponse {
  leads: Lead[]
  total: number
  limit: number
  offset: number
}

// Salesperson response type
interface Salesperson {
  email: string
  name: string
}

interface SalespersonsResponse {
  salespersons: Salesperson[]
  total: number
}

// Transform backend response to frontend PaginatedResponse format
const transformLeadsResponse = (
  backendData: BackendLeadsResponse,
  page: number,
  pageSize: number
): PaginatedResponse<Lead> => ({
  items: backendData.leads || [],
  total: backendData.total || 0,
  page: page,
  page_size: pageSize,
  total_pages: Math.ceil((backendData.total || 0) / pageSize),
})

export const leadsApi = {
  getAll: async (filters: LeadFilters = {}) => {
    const page = filters.page || 1
    const pageSize = filters.page_size || 25
    const offset = (page - 1) * pageSize

    // Build params properly - translate frontend filters to backend API params
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: Record<string, any> = {
      limit: pageSize,
      offset: offset,
    }

    // Search
    if (filters.search) {
      params.search = filters.search
    }

    // Status filter - passes directly to backend
    // Backend handles special values: 'contacted', 'not_contacted', 'existing_client'
    if (filters.status) {
      params.status = filters.status
    }

    // Qualification status filter - passes directly to backend
    if (filters.qualification_status) {
      params.qualification_status = filters.qualification_status
    }

    // Source filter (riviera_applied, riviera_approved, fmcsa, manual, etc.)
    if (filters.source) {
      params.source = filters.source
    }

    // Lead type filter (newly_applied, newly_approved, approved_90_days, etc.)
    if (filters.lead_type) {
      params.lead_type = filters.lead_type
    }

    // BDM/Salesperson filter
    if (filters.bdm_email) {
      params.bdm_email = filters.bdm_email
    }

    // Date filters
    if (filters.date_from) {
      params.created_from = filters.date_from
    }
    if (filters.date_to) {
      params.created_to = filters.date_to
    }

    // Sorting
    if (filters.sort_by) {
      params.sort_by = filters.sort_by
    }
    if (filters.sort_order) {
      params.sort_order = filters.sort_order
    }

    // Review queue filter
    if (filters.requires_review !== undefined) {
      params.requires_review = filters.requires_review
    }

    const response = await apiClient.get<BackendLeadsResponse>('/api/dashboard/leads/recent', {
      params,
    })

    return {
      ...response,
      data: transformLeadsResponse(response.data, page, pageSize),
    }
  },

  getById: (id: string) =>
    apiClient.get<Lead>(`/api/dashboard/leads/${id}`),

  create: (data: Partial<Lead>) =>
    apiClient.post<Lead>('/api/dashboard/leads/create', data),

  update: (id: string, data: Partial<Lead>) =>
    apiClient.put<Lead>(`/api/dashboard/leads/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/api/dashboard/leads/${id}`),

  updateStatus: (id: string, status: string) =>
    apiClient.put(`/api/dashboard/leads/${id}/status`, { status }),

  // Bulk push iterates through leads individually since there's no bulk endpoint
  bulkPush: async (leadIds: string[], queue: string, options?: { lead_type?: string; channel?: string; preferred_language?: string; bypass_cooldown?: boolean }) => {
    const results = await Promise.allSettled(
      leadIds.map(leadId =>
        apiClient.post(`/api/queue/push/${leadId}`, {
          agent: queue,
          lead_type: options?.lead_type || undefined,
          channel: options?.channel || undefined,
          preferred_language: options?.preferred_language || undefined,
          bypass_cooldown: options?.bypass_cooldown || false,
        })
      )
    )
    const successCount = results.filter(r => r.status === 'fulfilled').length
    const failedCount = results.filter(r => r.status === 'rejected').length

    // Collect error messages from failed pushes
    const errorMessages: string[] = []
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        const error = result.reason as { response?: { data?: { detail?: string } } }
        const detail = error?.response?.data?.detail || 'Unknown error'
        errorMessages.push(`Lead ${leadIds[index]}: ${detail}`)
      }
    })

    // Determine overall success status
    const allFailed = successCount === 0 && failedCount > 0

    let message = `Pushed ${successCount} lead(s) to ${queue} queue.`
    if (failedCount > 0) {
      message += ` ${failedCount} failed.`
      if (errorMessages.length > 0 && errorMessages.length <= 3) {
        message += ` Errors: ${errorMessages.join('; ')}`
      } else if (errorMessages.length > 3) {
        message += ` First error: ${errorMessages[0]}`
      }
    }

    return {
      data: {
        success: !allFailed,
        successCount,
        failedCount,
        errorMessages,
        message
      }
    }
  },

  bulkDelete: (leadIds: string[]) =>
    apiClient.post('/api/dashboard/leads/batch-delete', { lead_ids: leadIds }),

  bulkUpdateStatus: (leadIds: string[], status: string) =>
    apiClient.put('/api/dashboard/leads/bulk-status', { lead_ids: leadIds, status }),

  // Single lead push with options
  // Uses /api/queue/push endpoint with 'agent' field (qualification, engagement, advisor)
  pushToQueue: (leadId: string, queue: string, options?: { lead_type?: string; channel?: string; preferred_language?: string; bypass_cooldown?: boolean }) =>
    apiClient.post(`/api/queue/push/${leadId}`, {
      agent: queue, // Backend expects 'agent' not 'queue'
      lead_type: options?.lead_type || undefined,
      channel: options?.channel || undefined,
      preferred_language: options?.preferred_language || undefined,
      bypass_cooldown: options?.bypass_cooldown || false,
    }),

  // Get distinct salespersons (BDMs) from leads
  getSalespersons: () =>
    apiClient.get<SalespersonsResponse>('/api/dashboard/leads/salespersons'),

  // Bulk reassign leads to a different user
  bulkReassign: (leadIds: string[], assignedToId: number | null, bdmEmail?: string) =>
    apiClient.post<{
      success: boolean
      reassigned_count: number
      failed_count: number
      failed_ids: number[]
      access_denied_count: number
      access_denied_ids: number[]
      message: string
    }>('/api/dashboard/leads/bulk-reassign', {
      lead_ids: leadIds.map(id => parseInt(id, 10)),
      assigned_to_id: assignedToId,
      bdm_email: bdmEmail,
    }),

  // ===== Lead Review Queue =====

  // Get leads requiring human review
  getReviewQueue: (params?: { limit?: number; offset?: number }) =>
    apiClient.get<{
      leads: Lead[]
      total: number
      limit: number
      offset: number
    }>('/api/dashboard/leads/review-queue', { params }),

  // Get review queue statistics
  getReviewStats: () =>
    apiClient.get<{ pending_review: number }>('/api/dashboard/leads/review-queue/stats'),

  // Get lead with review context (transcript, recording URL)
  getReviewContext: (id: string) =>
    apiClient.get<{
      lead: Lead & {
        requires_review?: boolean
        review_status?: string
        review_notes?: string
        review_source_call_id?: string
      }
      call_context?: {
        call_id: string
        recording_url?: string
        transcript?: string
        duration_seconds?: number
        answered_by?: string
        summary?: string
      }
    }>(`/api/dashboard/leads/${id}/review-context`),

  // Approve a lead from the review queue
  approve: (id: string, data?: { review_notes?: string }) =>
    apiClient.post<{
      success: boolean
      message: string
      lead_id: number
    }>(`/api/dashboard/leads/${id}/approve`, data || {}),

  // Reject a lead from the review queue
  reject: (id: string, data?: { review_notes?: string }) =>
    apiClient.post<{
      success: boolean
      message: string
      lead_id: number
    }>(`/api/dashboard/leads/${id}/reject`, data || {}),
}
